import { useEffect, useRef } from 'react'
import { useAuth } from '@cravo/auth'
import {
  bootstrapPushIfRegistered,
  deregisterPushNotifications,
  isPushRegisteredForUser,
  registerPushNotifications,
} from '@/lib/push-notifications'

/**
 * Reacts to auth changes:
 *  - When a vendor authenticates AND has previously granted notification
 *    permission, silently register an FCM token so backend pushes start
 *    flowing to this device.
 *  - When the vendor logs out, deregister the token.
 *
 * Asking for permission is left to OrderAlertSettings (explicit user gesture).
 */
export function PushNotificationsBootstrap() {
  const { vendor, status } = useAuth()
  const lastUserIdRef = useRef<string | null>(null)
  const bootstrappedRef = useRef(false)

  useEffect(() => {
    if (status !== 'authenticated' || !vendor) return
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return

    // Bootstrap any existing token + foreground listener — but only AFTER
    // the user is authenticated. Running this on mount caused stale tokens
    // (from a previous session) to be replayed while the new login was still
    // initializing, triggering backend SESSION_NOT_FOUND errors.
    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true
      void bootstrapPushIfRegistered()
    }

    const userId = vendor.id

    if (lastUserIdRef.current === userId && isPushRegisteredForUser(userId)) {
      return
    }

    lastUserIdRef.current = userId
    void registerPushNotifications(userId)
  }, [vendor, status])

  useEffect(() => {
    if (status === 'unauthenticated') {
      lastUserIdRef.current = null
      bootstrappedRef.current = false
      void deregisterPushNotifications()
    }
  }, [status])

  return null
}
